// tempurpedic's Oscar order-detail tracker, shared by the thank-you (order
// confirmation) and account order pages (the `variant` flag selects the two
// line-markup layouts). Status decisions and labels resolve server-side
// (TPOrderTrackerInterface) except the first-ship tracking sentences, which
// are client-side ttag strings; this file is otherwise pure markup + the two
// interactive islands (the delivery tracker modal and the cancel-order button).
//
// The collapsible summary panel owns its open/close state: clicking the
// `.order-summary-details__info-column--order-details-btn` button flips
// `order-summary-details__header--active` on the header, the `--close` modifier
// on the button, its aria-expanded, and the panel's aria-hidden.
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { OrderCancelButton } from "tsi-common-react/src/apps/customer-account/OrderCancelButton/index";
import {
    OrderDetailsTrackerModal,
    getIcon,
} from "tsi-common-react/src/apps/customer-account/OrderDetailsTrackerModal/index";
import { jt, t } from "ttag";

import { interfaces } from "@reactivated";

type ITracker = Immutable<interfaces.TPOrderTrackerInterface>;
type IConfig = ITracker["config"];
type ILabels = ITracker["labels"];
type ISummary = ITracker["summary"];
type ISection = ITracker["sections"][number];
type ILine = ISection["lines"][number];
type ILineTracker = NonNullable<ILine["tracker"]>;

const SummaryPanel = (
    props: Immutable<{ summary: ISummary; labels: ILabels }>,
): React.JSX.Element => {
    const { summary, labels } = props;
    const panelId = `order-details-panel-${summary.order_pk}`;
    const [expanded, setExpanded] = React.useState(false);
    return (
        <div className="order-summary-details details">
            <div
                className={
                    expanded
                        ? "order-summary-details__header order-summary-details__header--active"
                        : "order-summary-details__header"
                }
            >
                <div className="order-summary-details__info">
                    <h3 className="order-summary-details__order-date">
                        {summary.order_date}
                    </h3>
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-number">
                        {labels.order_number} {summary.order_number}
                    </div>
                    <div className="order-summary-details__info-column order-summary-details__info-column--total-price-wrapper">
                        {labels.total}{" "}
                        <span className="order-summary-details__info-column--total-price">
                            {summary.total_display}
                        </span>
                    </div>
                </div>

                <div className="order-summary-details__info">
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-actions">
                        {summary.cancel_button && (
                            <OrderCancelButton {...summary.cancel_button} />
                        )}
                    </div>
                    {summary.order_issue_url && (
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={summary.order_issue_url}
                            className="order-summary-details__order-issues-link"
                            title={summary.order_issue_title}
                        >
                            {summary.order_issue_title}
                        </a>
                    )}
                    <a
                        className="order-summary-details__order-issues-link"
                        href={summary.review_products_url}
                    >
                        {labels.review_items}
                    </a>
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-details">
                        <button
                            type="button"
                            className={
                                expanded
                                    ? "order-summary-details__info-column--order-details-btn order-summary-details__info-column--order-details-btn--close"
                                    : "order-summary-details__info-column--order-details-btn"
                            }
                            aria-controls={panelId}
                            aria-expanded={expanded ? "true" : "false"}
                            onClick={() => {
                                setExpanded((value) => !value);
                            }}
                        >
                            <span className="order-summary-details__order-details">
                                {labels.order_details}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <div
                className="order-summary-details__columns"
                id={panelId}
                aria-hidden={expanded ? "false" : "true"}
            >
                <div className="order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {labels.shipping_address}
                    </h4>
                    <div className="order-summary-details__copy-block order-summary-details__address">
                        <div>
                            {summary.shipping_first_name}{" "}
                            {summary.shipping_last_name}
                        </div>
                        <div>
                            {summary.shipping_line1}
                            {summary.shipping_line2 && (
                                <>, {summary.shipping_line2}</>
                            )}
                            {summary.shipping_line3 && (
                                <>, {summary.shipping_line3}</>
                            )}
                        </div>
                        <div>
                            {summary.shipping_line4}, {summary.shipping_state}{" "}
                            {summary.shipping_postcode}
                            <br />
                            {summary.shipping_phone}
                        </div>
                    </div>
                </div>

                <div className="order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {labels.payment_method}
                    </h4>
                    <div className="order-summary-details__copy-block order-summary-details__payment">
                        {summary.cards.map((card, i) => (
                            <div
                                key={`card-${i}`}
                                className="order-summary-details__cc_payment"
                            >
                                <span className="order-summary-details__card_type">
                                    {card.card_type_name}
                                </span>{" "}
                                <span className="order-summary-details__card_mask">
                                    {card.ending_label}
                                </span>
                                {card.show_amount && (
                                    <span className="order-summary-details__amount">
                                        {card.amount_display}
                                    </span>
                                )}
                            </div>
                        ))}
                        {summary.financing_transfers.map((transfer, i) => (
                            <div
                                key={`fin-${i}`}
                                className="order-summary-details__financing_payment"
                            >
                                <div className="order-summary-details__account">
                                    <span>{transfer.account_label}</span>
                                    {transfer.show_amount && (
                                        <span className="order-summary-details__amount">
                                            {transfer.amount_display}
                                        </span>
                                    )}
                                </div>
                                {transfer.disclosure && (
                                    <span className="order-summary-details__terms">
                                        {labels.terms} {transfer.disclosure}
                                    </span>
                                )}
                            </div>
                        ))}
                        {summary.is_pay_later && (
                            <div className="order-summary-details__pay-later">
                                <p>
                                    <em>{labels.pay_later}</em>
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="order-summary-details__breakdown order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {labels.order_summary}
                    </h4>
                    <div className="order-summary-details__copy-block">
                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                {labels.subtotal}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.subtotal_display}
                            </div>
                        </div>
                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                {labels.shipping}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.shipping_display}
                            </div>
                        </div>
                        {summary.is_tax_known ? (
                            summary.tax_rows.map((group, i) => (
                                <div
                                    key={`tax-${i}`}
                                    className="order-summary-details__breakdown-row"
                                >
                                    <div className="order-summary-details__breakdown-title">
                                        {group.name}
                                    </div>
                                    <div className="order-summary-details__breakdown-number">
                                        {group.amount_display}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="order-summary-details__breakdown-row">
                                <div className="order-summary-details__breakdown-title">
                                    {labels.tax}
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {labels.tax_pending}
                                </div>
                            </div>
                        )}
                        <div className="order-summary-details__breakdown-row">
                            {summary.has_discount && (
                                <>
                                    <div className="order-summary-details__breakdown-title">
                                        {labels.discount}
                                    </div>
                                    <div className="order-summary-details__breakdown-number order-summary-details__breakdown-number--cerise">
                                        {summary.discount_display}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="order-summary-details__breakdown-row order-summary-details__breakdown-row--total">
                            <div className="order-summary-details__breakdown-title">
                                {labels.order_total}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.order_total_display}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TrackerSteps = (
    props: Immutable<{ tracker: ILineTracker; config: IConfig }>,
): React.JSX.Element => {
    const { tracker, config } = props;
    const modal = tracker.show_modal && (
        <OrderDetailsTrackerModal
            deliverySteps={
                (tracker.is_white_glove
                    ? config.freight_delivery_steps
                    : config.small_package_delivery_steps
                )?.value ?? []
            }
            modalHead={
                tracker.is_white_glove
                    ? tracker.shipping_method
                    : t`UPS Delivery`
            }
            modalSubHead={
                config.order_detail_tracker_modal_delivery_explanation || null
            }
            currentStep={tracker.current_step || undefined}
        />
    );
    return (
        <>
            {tracker.is_in_progress ? (
                <h3 className="tracker-step-title">
                    {tracker.heading}
                    {modal}
                </h3>
            ) : (
                tracker.heading && <h3>{tracker.heading}</h3>
            )}
            {tracker.show_inline_steps && (
                <div className="tracker-steps">
                    {tracker.inline_steps.map((step, i) => {
                        const icon = getIcon(step.tracker_step);
                        return (
                            <div
                                key={i}
                                className={`tracker-step${step.is_current ? " current-step" : ""}`}
                            >
                                {icon && (
                                    <SVG
                                        className="step-icon"
                                        aria-hidden="true"
                                        src={icon}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

const TrackingInfo = (
    props: Immutable<{ line: ILine; labels: ILabels }>,
): React.JSX.Element => {
    const { line, labels } = props;
    const info = line.tracking_info;
    if (!info) {
        return <></>;
    }
    if (info.is_freight) {
        if (!info.tracking_url) {
            return <></>;
        }
        return (
            <div>
                {line.line_variant === "customer" && (
                    <span className="product__shipping-status--pnp-number">
                        {info.tracking_number}
                    </span>
                )}
                <a
                    className="button product__shipping-status--wg-tracking product__shipping-status--tracking-link al-shipping--wg-tracking"
                    href={info.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {labels.track_shipment}
                </a>
            </div>
        );
    }
    if (!info.tracking_number) {
        return <></>;
    }
    return (
        <>
            <p className="product__shipping-status product__shipping-status--carrier">
                {info.carrier}
                <span className="product__shipping-status--tracking-number">
                    {info.tracking_number}
                </span>
            </p>
            <a
                className="button product__shipping-status--tracking-link"
                href={info.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
            >
                {labels.track_shipment}
            </a>
        </>
    );
};

const LinePrice = (props: Immutable<{ line: ILine }>): React.JSX.Element => {
    const { line } = props;
    if (line.line_variant === "checkout") {
        return (
            <>
                {line.is_discounted ? (
                    <>
                        <span className="ada-screenreader-only">
                            {t`Original Price:`}
                        </span>
                        <span className="product__details__copy--strike">
                            {line.msrp_display}
                        </span>
                        <span className="ada-screenreader-only">
                            {t`Current Price:`}
                        </span>
                        <span className="product__details__copy--razzmatazz">
                            {line.actual_display}
                        </span>
                    </>
                ) : (
                    <span>{line.actual_display}</span>
                )}
                {line.addon_display && (
                    <>
                        <span className="ada-screenreader-only">
                            {t`Add-on Price:`}
                        </span>
                        <span>+{line.addon_display}</span>
                    </>
                )}
            </>
        );
    }
    const addonPrice = line.addon_display && (
        <>
            {" "}
            <span className="ada-screenreader-only">{t`Add-on Price:`}</span>
            <span className="product__retail-price">+{line.addon_display}</span>
        </>
    );
    return (
        <div className="product__price-container">
            {line.is_discounted ? (
                <>
                    <span className="ada-screenreader-only">
                        {t`Current Price:`}
                    </span>
                    <span className="product__actual-price">
                        {line.actual_display}
                    </span>
                    {addonPrice}{" "}
                    <span className="ada-screenreader-only">
                        {t`Original Price:`}
                    </span>
                    <span className="product__retail-price product__retail-price--strike">
                        {line.msrp_display}
                    </span>
                </>
            ) : (
                <>
                    <span className="product__retail-price">
                        {line.actual_display}
                    </span>
                    {addonPrice}
                </>
            )}
        </div>
    );
};

const OrderLine = (
    props: Immutable<{ line: ILine; config: IConfig; labels: ILabels }>,
): React.JSX.Element => {
    const { line, config, labels } = props;
    const isCheckout = line.line_variant === "checkout";
    // Hoisted out of the jt interpolation (conditionals inside a jt template
    // crash ttag extraction) and so the sentence stays one translatable unit.
    const orderDetailsLink = (
        <a key="order-details" href={line.order_list_url}>
            {t`view your order details`}
        </a>
    );
    return (
        <div
            className={`order-detail${line.section_divider ? " section-divider" : ""}`}
            role="group"
            aria-label={line.aria_label}
        >
            <div className="section__left products__results">
                <div className="product__image">
                    <a
                        href={line.pdp_url}
                        {...(isCheckout
                            ? { title: line.image_link_title }
                            : {})}
                    >
                        {/* No <img> for a blank url, so a product-less line
                            shows an empty link, not a broken-image icon. */}
                        {line.image_url && (
                            <img
                                src={line.image_url}
                                alt={line.image_alt}
                                {...(isCheckout
                                    ? {}
                                    : { className: "responsive-img" })}
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </a>
                </div>
                <div className="product__details">
                    <div className="product__details__copy">
                        <h4 className="product__details__title">
                            {line.title}
                        </h4>
                        {line.attribute_summary && (
                            <>
                                {line.attribute_summary}
                                <br />
                            </>
                        )}
                        {labels.qty} {line.quantity}
                        <br />
                        {line.show_price && <LinePrice line={line} />}
                        {line.is_bundled && (
                            <div className="order-detail__bundle-callout">
                                {line.bundle_callout}
                            </div>
                        )}
                        {line.show_reviews && (
                            <div className="product__reviews">
                                {line.show_write_review && (
                                    <>
                                        <a
                                            href={line.write_review_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {labels.write_review}
                                        </a>
                                        {line.review_divider && (
                                            <>
                                                {" "}
                                                <span className="product__copy-divider">
                                                    &#124;
                                                </span>{" "}
                                            </>
                                        )}
                                    </>
                                )}
                                {line.show_registration_link && (
                                    <a
                                        href={line.registration_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {labels.register_product}
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                className={`section__right ${isCheckout ? "products__status" : "product__status"}`}
            >
                {line.display_progress_bar && (
                    <>
                        {line.status_block === "pay_later" && (
                            <a
                                href={line.complete_payment_url}
                                className="order-detail__complete-payment-link"
                            >
                                {labels.complete_payment}
                            </a>
                        )}
                        {line.status_block === "tracker" && line.tracker && (
                            <TrackerSteps
                                tracker={line.tracker}
                                config={config}
                            />
                        )}
                        {line.status_block === "unavailable" && (
                            <div className="product__status__copy">
                                <p className="product__shipping-status">
                                    {line.unavailable_text}
                                </p>
                            </div>
                        )}
                    </>
                )}

                <div className="product__status__copy">
                    {line.display_progress_bar && (
                        <>
                            {line.tracking_copy === "first_ship" &&
                                (isCheckout ? (
                                    <p className="product__shipping-status--tracking-info-copy">
                                        {jt`After your item ships, ${orderDetailsLink} for tracking and delivery information`}
                                    </p>
                                ) : (
                                    <p className="product__shipping-status--tracking-info-copy">
                                        {t`Tracking information will be provided once your order ships`}
                                    </p>
                                ))}
                            {line.tracking_copy === "tracking_info" && (
                                <TrackingInfo line={line} labels={labels} />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const Section = (
    props: Immutable<{
        section: ISection;
        variant: ITracker["variant"];
        config: IConfig;
        labels: ILabels;
    }>,
): React.JSX.Element => {
    const { section, variant, config, labels } = props;
    const lines = (
        <div className="section products">
            {section.lines.map((line, i) => (
                <OrderLine
                    key={i}
                    line={line}
                    config={config}
                    labels={labels}
                />
            ))}
        </div>
    );
    if (variant === "thank_you") {
        return lines;
    }
    const bodyClass =
        section.section_kind === "shipped"
            ? "order-summary__body order-summary__body--shipping-events-"
            : "order-summary__body order-summary__body--unshipped-order";
    return <div className={bodyClass}>{lines}</div>;
};

export const OrderDetailTracker = (
    props: Immutable<{ data: ITracker }>,
): React.JSX.Element => {
    const { data } = props;
    return (
        <div
            className={
                data.variant === "thank_you"
                    ? "thank-you-summary"
                    : "order-summary"
            }
        >
            <SummaryPanel summary={data.summary} labels={data.labels} />
            {data.sections.map((section, i) => (
                <Section
                    key={i}
                    section={section}
                    variant={data.variant}
                    config={data.config}
                    labels={data.labels}
                />
            ))}
        </div>
    );
};
